import { stripDiacritics } from '@realadvisor/transform/strip-diacritics';

// Business entity suffixes (AG, SA, GmbH, etc.) to strip from agency names
const LEGAL_SUFFIXES = [
  'AG',
  'SA',
  'GMBH',
  'SARL',
  'SÀRL',
  'SRL',
  'SAGL',
  'SL',
  'SLU',
];

const LEGAL_SUFFIX_PATTERN = new RegExp(
  `\\s*(${LEGAL_SUFFIXES.join('|')})\\s*$`,
  'i',
);

const FRANCHISE_BRANDS: { name: string; variants: string[] }[] = [
  { name: 'RE/MAX', variants: ['REMAX', 'RE/MAX'] },
  {
    name: 'ENGEL & VÖLKERS',
    variants: ['ENGEL&VÖLKERS', 'ENGEL & VÖLKERS', 'ENGEL VÖLKERS'],
  },
  { name: 'CENTURY 21', variants: ['CENTURY21', 'CENTURY 21'] },
  { name: 'COLDWELL BANKER', variants: ['COLDWELL BANKER'] },
  { name: 'KELLER WILLIAMS', variants: ['KELLER WILLIAMS'] },
  { name: "SOTHEBY'S", variants: ["SOTHEBY'S", 'SOTHEBYS'] },
  { name: 'BARNES', variants: ['BARNES'] },
];

// Prefixes that scrapers inconsistently add/remove
const AGENCY_PREFIXES = [
  'INMOBILIARIA',
  'AGENCIA',
  'AGENCIA INMOBILIARIA',
  'GRUPO',
  'AGENCE',
  'AGENCE IMMOBILIÈRE',
  'IMMOBILIARE',
];

// Separators that split name parts (don't split "/" between digits like "14/20")
const PART_SEPARATORS = /\s+[-–—|,:\\]+\s*|[–—|,:\\]+|\s+I\s+/;
const SLASH_BETWEEN_NON_DIGITS = /(?<!\d)\/(?!\d)/;

type FranchiseBrand = (typeof FRANCHISE_BRANDS)[number];

// String normalization helpers
const normalizeWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

const stripLegalSuffix = (text: string) =>
  normalizeWhitespace(text.replace(LEGAL_SUFFIX_PATTERN, ''));

const cleanForPattern = (text: string) =>
  normalizeWhitespace(text.replace(/[^0-9\p{L}\s]/gu, ' '));

const splitOnSeparators = (text: string) =>
  text
    .split(SLASH_BETWEEN_NON_DIGITS)
    .flatMap(part => part.split(PART_SEPARATORS))
    .map(p => p.trim())
    .filter(Boolean);

// Helper to add a prefix pattern with its accent-normalized variant
const addPrefixWithAccentVariant = (candidates: string[], pattern: string) => {
  candidates.push(`${pattern}%`);
  const noAccents = stripDiacritics(pattern);
  if (noAccents !== pattern) {
    candidates.push(`${noAccents}%`);
  }
};

// Preserve apostrophes in patterns for names like L'Immobilier, D'Azur
const preserveApostrophes = (text: string) =>
  text.replace(/(\p{L})[''](\p{L})/gu, "$1'$2");

// Franchise brand detection and extraction
const findFranchiseBrand = (text: string) => {
  const upper = text.toUpperCase();
  return (
    FRANCHISE_BRANDS.find(brand =>
      brand.variants.some(variant => upper.includes(variant)),
    ) ?? null
  );
};

const extractBrandName = (text: string, brand: FranchiseBrand) => {
  for (const variant of brand.variants) {
    const escapedVariant = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(${escapedVariant}[^-–—|,:\\\\]*)`, 'i');
    const match = text.match(pattern);
    if (match) {
      return normalizeWhitespace(match[1]);
    }
  }
  return text;
};

const stripAgencyPrefix = (text: string) => {
  const upper = text.toUpperCase();
  const matchedPrefix = AGENCY_PREFIXES.find(prefix =>
    upper.startsWith(prefix + ' '),
  );
  if (!matchedPrefix) {
    return null;
  }
  const remainder = text.slice(matchedPrefix.length).trim();
  return remainder.length >= 3 ? remainder : null;
};

// Pattern generation helpers
const addPatternVariants = (
  candidates: string[],
  cleaned: string,
  includeCompound: boolean,
) => {
  const lower = cleaned.toLowerCase();

  if (lower.length < 3) {
    return;
  }

  addPrefixWithAccentVariant(candidates, lower);

  if (includeCompound) {
    const compound = lower.replace(/\s+/g, '');
    if (compound !== lower && compound.length >= 3) {
      addPrefixWithAccentVariant(candidates, compound);
    }
  }

  // Handle RE/MAX ↔ REMAX variations
  if (lower.includes('remax')) {
    candidates.push(`${lower.replace(/remax/g, 're/max')}%`);
  } else if (lower.includes('re/max')) {
    candidates.push(`${lower.replace(/re\/max/g, 'remax')}%`);
  }

  const withoutSuffix = stripLegalSuffix(cleaned).toLowerCase();
  if (withoutSuffix !== lower && withoutSuffix.length >= 3) {
    addPrefixWithAccentVariant(candidates, withoutSuffix);
  }

  const withoutPrefix = stripAgencyPrefix(cleaned);
  if (withoutPrefix) {
    const cleanedWithoutPrefix = cleanForPattern(withoutPrefix).toLowerCase();
    if (cleanedWithoutPrefix.length >= 3) {
      candidates.push(`${cleanedWithoutPrefix}%`);
    }
  }
};

/**
 * Generate lowercase prefix patterns for case-insensitive prefix search.
 * Works with: CREATE INDEX ON cma_listings USING btree (lower(agency_name) text_pattern_ops)
 */
export const makeLikePrefixCandidates = (name?: string | null) => {
  if (!name) {
    return [];
  }

  const normalized = normalizeWhitespace(name);
  const candidates: string[] = [];
  const franchiseBrand = findFranchiseBrand(normalized);
  const nameVariants: string[] = [];

  if (franchiseBrand) {
    const brandName = extractBrandName(normalized, franchiseBrand);
    nameVariants.push(brandName);

    const firstPart = splitOnSeparators(normalized)[0] ?? normalized;
    if (firstPart !== brandName && firstPart.length >= 3) {
      nameVariants.push(firstPart);
    }

    for (const variant of franchiseBrand.variants) {
      const cleanedVariant = cleanForPattern(variant);
      if (cleanedVariant.length >= 3) {
        candidates.push(`${cleanedVariant.toLowerCase()}%`);
      }
    }
  } else {
    nameVariants.push(splitOnSeparators(normalized)[0] ?? normalized);
  }

  for (const nameVariant of nameVariants) {
    const cleaned = cleanForPattern(nameVariant);
    addPatternVariants(candidates, cleaned, true);

    // Also add pattern with preserved apostrophes for names like L'Immobilier
    const withApostrophes = preserveApostrophes(nameVariant.toLowerCase());
    if (withApostrophes !== cleaned.toLowerCase()) {
      addPrefixWithAccentVariant(candidates, withApostrophes);
    }

    const words = cleaned.split(/\s+/);

    // Drop last word which is often a location suffix
    if (words.length >= 3) {
      const withoutLastWord = words.slice(0, -1).join(' ');
      if (withoutLastWord.length >= 5) {
        addPatternVariants(candidates, withoutLastWord, false);
      }
    }

    // Strip "in [Location]" suffix pattern
    const withoutInLocation = cleaned.replace(/\s+in\s+\S+$/i, '');
    if (withoutInLocation !== cleaned && withoutInLocation.length >= 5) {
      addPatternVariants(candidates, withoutInLocation, false);
    }

    // First word only helps match when scrapers use different suffixes
    const firstWord = words[0];
    if (words.length >= 2 && firstWord.length >= 4) {
      addPrefixWithAccentVariant(candidates, firstWord.toLowerCase());
    }
  }

  return Array.from(new Set(candidates));
};
